import {
  CONTACT_FIELD_LABEL,
  CONTACT_FIELD_MAX_LENGTH,
  ContactFieldKey,
  codePointLength,
} from './contactFieldLimits'

const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/
const asciiPrintableRegex = /^[\x20-\x7E]+$/

// 文字数チェック対象のスカラー項目（externalNumbers 内の項目は別途チェックする）
const lengthCheckFields: ContactFieldKey[] = [
  'lastName',
  'firstName',
  'lastNameKana',
  'firstNameKana',
  'email',
  'organization',
  'department',
  'position',
  'mobilePhone',
  'telExtension',
  'altEmail',
  'fax',
  'postalCode',
  'address1',
  'address2',
  'city',
  'prefecture',
  'country',
  'site',
  'linkedIn',
  'facebook',
  'twitter',
  'skype',
  'slack',
  'github',
  'youtube',
  'tiktok',
  'spotify',
  'discord',
  'memo',
]

const phoneLikeRegex = /^[0-9#*-]*$/

export const sanitizePhoneLike = (value: string) => {
  return (value ?? '').replace(/[^0-9#*-]/g, '')
}

export const isPhoneLike = (value: string) => {
  const v = (value ?? '').trim()
  if (v === '') return true
  return phoneLikeRegex.test(v)
}

type ExternalNumber = {
  externalPhoneNumber: string
  labelCode: number | null
  customLabel: string
  displayOrder: number
}

type ContactFormFields = Record<
  string,
  string | boolean | ExternalNumber[] | null | undefined
>

export const validateContactForm = (fields: ContactFormFields) => {
  const errors: string[] = []

  if (!fields.lastName) errors.push('必須入力です')

  const email = fields.email
  if (email && typeof email === 'string' && !emailRegex.test(email))
    errors.push('Eメールアドレスを入力してください')

  const altEmail = fields.altEmail
  if (altEmail && typeof altEmail === 'string' && !emailRegex.test(altEmail))
    errors.push('その他Eメールを入力してください')

  const postalCode = fields.postalCode
  if (
    postalCode &&
    typeof postalCode === 'string' &&
    !asciiPrintableRegex.test(postalCode)
  )
    errors.push('郵便番号は英数字記号で入力してください')

  const snsFields = [
    'linkedIn',
    'facebook',
    'twitter',
    'skype',
    'slack',
    'github',
    'youtube',
    'tiktok',
    'spotify',
    'discord',
  ]

  snsFields.forEach((key) => {
    const value = fields[key]
    if (value && typeof value === 'string' && !asciiPrintableRegex.test(value)) {
      errors.push(`${key} IDを入力してください`)
    }
  })

  // 各スカラー項目の文字数チェック（DB の VARCHAR 桁数を超える入力を弾く）
  lengthCheckFields.forEach((key) => {
    const value = fields[key]
    if (typeof value !== 'string') return

    const max = CONTACT_FIELD_MAX_LENGTH[key]
    if (codePointLength(value) > max) {
      errors.push(`「${CONTACT_FIELD_LABEL[key]}」は${max}文字以内で入力してください`)
    }
  })

  const externalNumbers = fields.externalNumbers
  if (externalNumbers && Array.isArray(externalNumbers)) {
    externalNumbers.forEach((row) => {
      const phone = (row.externalPhoneNumber ?? '').trim()

      if (phone !== '') {
        if (!isPhoneLike(phone)) {
          errors.push('電話番号は数字、#、-、* のみで入力してください')
        }
        if (codePointLength(phone) > CONTACT_FIELD_MAX_LENGTH.externalPhoneNumber) {
          errors.push(
            `「${CONTACT_FIELD_LABEL.externalPhoneNumber}」は${CONTACT_FIELD_MAX_LENGTH.externalPhoneNumber}文字以内で入力してください`,
          )
        }
      }

      // カスタムラベル（既定ラベル以外を入力した場合）の文字数チェック
      const customLabel = row.customLabel ?? ''
      if (codePointLength(customLabel) > CONTACT_FIELD_MAX_LENGTH.customLabel) {
        errors.push(
          `「${CONTACT_FIELD_LABEL.customLabel}」は${CONTACT_FIELD_MAX_LENGTH.customLabel}文字以内で入力してください`,
        )
      }
    })
  }

  // 同一メッセージの重複を除去（複数行で同じエラーが出ないようにする）
  return Array.from(new Set(errors)).join('\n')
}
